// Handyman category data definitions
import { EMERGENCY_TIMELINE_OPTIONS } from '../../data/constants';

// ==================== HANDYMAN: Hanging/Mounting/Assembly Prices ====================
export const HANDYMAN_MOUNT_ITEMS = {
  curtains_blinds: { low: 75, high: 200, label: "Curtains / Blinds", perUnit: "per set" },
  furniture: { low: 75, high: 250, label: "Furniture (bed, desk, dresser, etc.)", perUnit: "per item" },
  mirrors_artwork: { low: 50, high: 125, label: "Mirrors / Artwork", perUnit: "per item" },
  shelves: { low: 50, high: 150, label: "Shelves", perUnit: "per shelf" },
  tv_mount: { low: 200, high: 300, label: "TV (wall mount)", perUnit: "per TV" },
  other_mounting: { low: 75, high: 300, label: "Other", perUnit: "varies" }
};

// Item count bundles for handyman mounting
export const HANDYMAN_ITEM_COUNT_PRICES = {
  "1": { low: 75, high: 200 },
  "2-3": { low: 150, high: 400 },
  "4-6": { low: 300, high: 750 },
  "7+": { low: 600, high: 1200 }
};

// Wall type modifiers
export const WALL_TYPE_MODIFIERS = {
  drywall: { low: 0, high: 0 },
  brick: { low: 25, high: 75 },
  concrete: { low: 50, high: 125 },
  not_sure: { low: 0, high: 0 }
};

// TV size modifiers
export const TV_SIZE_MODIFIERS = {
  "under_55": { low: 0, high: 0 },
  "55-64": { low: 0, high: 25 },
  "65_plus": { low: 25, high: 75 }
};

export const FURNITURE_ANCHORING_MODIFIER = { low: 25, high: 75 };

// ==================== HANDYMAN: Doors & Locks Prices ====================
export const DOORS_LOCKS_ITEM_COUNT = {
  "1": { low: 75, high: 200 },
  "2-3": { low: 200, high: 450 },
  "4-6": { low: 400, high: 900 },
  "7+": { low: 800, high: 1500 }
};

export const EXTERIOR_DOOR_MODIFIER = { low: 50, high: 150 };
export const FRAMING_MODIFICATION_MODIFIER = { low: 100, high: 300 };

// ==================== HANDYMAN: Carpentry Prices ====================
export const CARPENTRY_SCOPE_PRICES = {
  small: { low: 100, high: 300 },
  medium: { low: 300, high: 700 },
  large: { low: 700, high: 1200 }
};

export const DRYWALL_FINISHING_MODIFIER = { low: 50, high: 150 };
export const PAINTING_INCLUDED_MODIFIER = { low: 100, high: 300 };

// ==================== HANDYMAN: Electrical Prices ====================
export const ELECTRICAL_FIXTURE_COUNT = {
  "1": { low: 80, high: 150 },
  "2-3": { low: 200, high: 450 },
  "4-6": { low: 400, high: 900 },
  "7+": { low: 800, high: 1400 }
};

export const CEILING_FIXTURE_MODIFIER = { low: 25, high: 75 };
export const EXPOSED_WIRING_MODIFIER = { low: 50, high: 100 };

// ==================== HANDYMAN: Plumbing Prices ====================
export const PLUMBING_FIXTURE_COUNT = {
  "1": { low: 75, high: 150 },
  "2-3": { low: 200, high: 450 },
  "4-6": { low: 400, high: 900 },
  "7+": { low: 800, high: 1500 }
};

export const ACTIVE_LEAK_MODIFIER = { low: 50, high: 150 };

// ==================== HANDYMAN: Interior Repair Prices ====================
export const INTERIOR_REPAIR_SERVICES = {
  drywall_repair: { low: 150, high: 300, label: "Drywall Repair (nail holes, patches up to 12\")" },
  patch_touchup_paint: { low: 200, high: 400, label: "Patch & Touch-Up Paint (1-2 walls)" },
  door_repairs: { low: 100, high: 250, label: "Door Repairs (hinges, knobs, alignment)" },
  fix_leaking_faucet: { low: 200, high: 300, label: "Fix Leaking Faucets / Toilets" },
  dishwasher_repair: { low: 200, high: 400, label: "Dishwasher Repair" },
  baseboard_molding: { low: 250, high: 450, label: "Baseboard or Molding Repair" },
  hang_blinds: { low: 35, high: 75, label: "Hang Blinds (2\" Standard)", perUnit: "each", isVariable: true },
  unclog_drains: { low: 125, high: 250, label: "Unclog Drains (sink, tub, shower)" },
  replace_outlets: { low: 45, high: 125, label: "Replace Outlets / Switches", perUnit: "each", isVariable: true },
  tv_mounting: { low: 225, high: 425, label: "TV Mounting" },
  smart_switches: { low: 125, high: 250, label: "Install Smart Switches / Thermostats" },
  mount_shelves: { low: 85, high: 175, label: "Mount Shelves, Mirrors, or Artwork" },
  install_curtain_rods: { low: 85, high: 150, label: "Install Curtain Rods or Shades" },
  power_washing: { low: 150, high: 300, label: "Power Washing (driveways, patios)" },
  weatherizing: { low: 125, high: 350, label: "Weatherizing (caulking, door sweeps)" },
  rental_turnover: { low: 400, high: 1200, label: "Rental Turnover Repairs", isVariable: true },
  install_dishwasher: { low: 250, high: 500, label: "Install Dishwasher (replacement)" },
  install_microwave: { low: 250, high: 350, label: "Install Over-the-Range Microwave" }
};

export const INTERIOR_REPAIR_QUANTITY = {
  "1": 1,
  "2-3": 2.5,
  "4-6": 5,
  "7+": 8
};

// ==================== HANDYMAN: Service Categories with Collapsible Groups ====================
export const HANDYMAN_SERVICE_CATEGORIES = [
  {
    id: "doors_hardware",
    name: "Doors & Hardware",
    emoji: "🚪",
    services: [
      { value: "door_install_repair", label: "Door installation, repair, or replacement", triggerFlow: "doors_locks", price: { low: 150, high: 350 } },
      { value: "door_staining_painting", label: "Door staining or painting", price: { low: 100, high: 250 } },
      { value: "door_threshold", label: "Door thresholds", price: { low: 75, high: 175 } },
      { value: "door_hinges", label: "Door hinges", triggerFlow: "doors_locks", price: { low: 50, high: 125 } },
      { value: "door_locks", label: "Door locks", triggerFlow: "doors_locks", price: { low: 75, high: 200 } },
      { value: "weather_stripping", label: "Weather stripping", price: { low: 75, high: 175 } },
      { value: "doorknob_install", label: "Doorknob installation", triggerFlow: "doors_locks", price: { low: 50, high: 125 } },
      { value: "interior_doors", label: "Interior doors", triggerFlow: "doors_locks", price: { low: 150, high: 350 } },
      { value: "cabinets", label: "Cabinets", triggerFlow: "carpentry", price: { low: 125, high: 400 } },
      { value: "vanities", label: "Vanities", price: { low: 200, high: 450 } },
      { value: "closets", label: "Closets", triggerFlow: "carpentry", price: { low: 150, high: 500 } }
    ]
  },
  {
    id: "bathroom",
    name: "Bathroom",
    emoji: "🚿",
    services: [
      { value: "toilet_install", label: "Toilet installation or replacement", triggerFlow: "small_plumbing", price: { low: 200, high: 500 } },
      { value: "bathroom_faucet", label: "Bathroom faucet installation", triggerFlow: "small_plumbing", price: { low: 100, high: 250 } },
      { value: "vanity_install", label: "Vanity removal and installation", price: { low: 250, high: 550 } },
      { value: "caulking_sealing", label: "Caulking and sealing", price: { low: 75, high: 175 } },
      { value: "shower_rods", label: "Shower rods", price: { low: 50, high: 100 } },
      { value: "towel_holders", label: "Towel holders", price: { low: 50, high: 125 } }
    ]
  },
  {
    id: "kitchen",
    name: "Kitchen",
    emoji: "🍽️",
    services: [
      { value: "kitchen_faucet", label: "Kitchen faucet installation", triggerFlow: "small_plumbing", price: { low: 100, high: 250 } },
      { value: "garbage_disposal", label: "Garbage disposal installation", triggerFlow: "small_plumbing", price: { low: 150, high: 350 } },
      { value: "dishwasher_install", label: "Dishwasher installation", price: { low: 250, high: 500 } },
      { value: "refrigerator_install", label: "Refrigerator installation", price: { low: 150, high: 300 } },
      { value: "stove_range_install", label: "Stove/range installation", price: { low: 175, high: 350 } }
    ]
  },
  {
    id: "electrical",
    name: "Electrical",
    emoji: "⚡",
    services: [
      { value: "thermostat_install", label: "Thermostat installation", triggerFlow: "small_electrical", price: { low: 125, high: 300 } },
      { value: "smoke_detector", label: "Smoke detector installation", price: { low: 75, high: 150 } },
      { value: "small_electrical", label: "Small electrical work", triggerFlow: "small_electrical", price: { low: 80, high: 250 } },
      { value: "outlets", label: "Outlets", triggerFlow: "small_electrical", price: { low: 80, high: 150 } },
      { value: "gfci_outlets", label: "GFCI outlets", triggerFlow: "small_electrical", price: { low: 100, high: 200 } },
      { value: "light_fixtures", label: "Light fixtures", triggerFlow: "small_electrical", price: { low: 100, high: 250 } },
      { value: "switches", label: "Switches", triggerFlow: "small_electrical", price: { low: 80, high: 150 } },
      { value: "ceiling_lights", label: "Ceiling lights", triggerFlow: "small_electrical", price: { low: 125, high: 300 } }
    ]
  },
  {
    id: "carpentry_interior",
    name: "Carpentry & Interior Repairs",
    emoji: "🔨",
    services: [
      { value: "shelving_install", label: "Shelving installation", triggerFlow: "carpentry", price: { low: 100, high: 300 } },
      { value: "baseboards", label: "Baseboards", triggerFlow: "carpentry", price: { low: 100, high: 300 } },
      { value: "drywall_patching", label: "Drywall / Sheetrock patching", triggerFlow: "carpentry", price: { low: 100, high: 350 } },
      { value: "drywall_replacement", label: "Drywall replacement", triggerFlow: "carpentry", price: { low: 200, high: 500 } },
      { value: "drywall_repair", label: "Drywall repair", triggerFlow: "carpentry", price: { low: 150, high: 300 } },
      { value: "paint_touchups", label: "Paint touch-ups", price: { low: 100, high: 300 } },
      { value: "interior_repairs", label: "Interior repairs", triggerFlow: "interior_repair", price: { low: 100, high: 400 } },
      { value: "attic_ladder", label: "Attic ladder installation", price: { low: 250, high: 500 } }
    ]
  },
  {
    id: "mounting_assembly",
    name: "Mounting & Assembly",
    emoji: "📺",
    services: [
      { value: "tv_mounting", label: "TV mounting/setup", triggerFlow: "hanging_mounting", price: { low: 200, high: 400 } },
      { value: "furniture_assembly", label: "Furniture assembly", triggerFlow: "hanging_mounting", price: { low: 75, high: 250 } },
      { value: "wall_mounting", label: "Wall mounting", triggerFlow: "hanging_mounting", price: { low: 75, high: 200 } },
      { value: "hanging_items", label: "Hanging artwork, mirrors, shelves, etc.", triggerFlow: "hanging_mounting", price: { low: 50, high: 175 } }
    ]
  },
  {
    id: "hvac",
    name: "HVAC",
    emoji: "❄️",
    services: [
      { value: "hvac_maintenance", label: "HVAC maintenance", price: { low: 150, high: 350 } },
      { value: "ac_unit_service", label: "A/C unit service", price: { low: 150, high: 400 } },
      { value: "filter_replacement", label: "Filter replacement", price: { low: 50, high: 125 } },
      { value: "minor_hvac_repairs", label: "Minor HVAC repairs", price: { low: 150, high: 400 } }
    ]
  },
  {
    id: "outdoor",
    name: "Outdoor Services",
    emoji: "🌳",
    services: [
      { value: "yard_work", label: "Yard work", price: { low: 100, high: 300 } },
      { value: "landscaping", label: "Landscaping", price: { low: 200, high: 600 } },
      { value: "masonry", label: "Masonry", price: { low: 200, high: 600 } },
      { value: "fence_repair", label: "Fence repair", price: { low: 150, high: 400 } },
      { value: "fence_staining", label: "Fence staining", price: { low: 200, high: 500 } },
      { value: "power_washing", label: "Power washing", price: { low: 150, high: 350 } },
      { value: "sidewalks", label: "Sidewalks", price: { low: 150, high: 400 } },
      { value: "driveways", label: "Driveways", price: { low: 200, high: 500 } },
      { value: "siding", label: "Siding", price: { low: 200, high: 600 } },
      { value: "patios", label: "Patios", price: { low: 200, high: 600 } },
      { value: "fences", label: "Fences", price: { low: 200, high: 600 } }
    ]
  },
  {
    id: "home_maintenance",
    name: "Home Maintenance",
    emoji: "🏠",
    services: [
      { value: "fireplace_cleaning", label: "Fireplace cleaning", price: { low: 150, high: 300 } },
      { value: "blind_install", label: "Blind installation", price: { low: 75, high: 200 } },
      { value: "general_handyman", label: "General handyman services", price: { low: 75, high: 500 } }
    ]
  }
];

// Build SERVICE_TO_FLOW_MAP programmatically
export const SERVICE_TO_FLOW_MAP = {};
HANDYMAN_SERVICE_CATEGORIES.forEach(cat => {
  cat.services.forEach(svc => {
    if (svc.triggerFlow) {
      SERVICE_TO_FLOW_MAP[svc.value] = svc.triggerFlow;
    }
  });
});

// Build HANDYMAN_SERVICE_PRICES
export const HANDYMAN_SERVICE_PRICES = {};
HANDYMAN_SERVICE_CATEGORIES.forEach(cat => {
  cat.services.forEach(svc => {
    HANDYMAN_SERVICE_PRICES[svc.value] = svc.price;
  });
});

// Rental turnover scope-based pricing
export const RENTAL_TURNOVER_SCOPE_PRICES = {
  light: { low: 400, high: 600 },
  moderate: { low: 600, high: 900 },
  extensive: { low: 900, high: 1200 }
};

// ==================== HANDYMAN TIMELINE ====================
export const HANDYMAN_TIMELINE_OPTIONS = [
  { value: "emergency", label: "Emergency - Same day / ASAP" },
  { value: "1-3_days", label: "Within 1-3 days" },
  { value: "1_week", label: "Within a week" },
  { value: "quote_only", label: "Just Getting a Quote" }
];

// ==================== SUB-FLOW DEFINITIONS ====================
// List of sub-flows that have detailed question paths
export const DETAILED_SUBFLOWS = [
  'interior_repair',
  'hanging_mounting', 
  'doors_locks',
  'carpentry',
  'small_electrical',
  'small_plumbing'
];

// ==================== QUESTION DEFINITIONS BY SUB-FLOW ====================

// Interior Repair Questions
export const INTERIOR_REPAIR_QUESTIONS = [
  {
    id: "interior_repair_services",
    question: "What service do you need?",
    type: "radio",
    options: [
      { value: "drywall_repair", label: "Drywall Repair (nail holes, patches up to 12\")" },
      { value: "patch_touchup_paint", label: "Patch & Touch-Up Paint (1-2 walls)" },
      { value: "door_repairs", label: "Door Repairs (hinges, knobs, alignment)" },
      { value: "fix_leaking_faucet", label: "Fix Leaking Faucets / Toilets" },
      { value: "dishwasher_repair", label: "Dishwasher Repair" },
      { value: "baseboard_molding", label: "Baseboard or Molding Repair" },
      { value: "hang_blinds", label: "Hang Blinds (2\" Standard)" },
      { value: "unclog_drains", label: "Unclog Drains (sink, tub, shower)" },
      { value: "replace_outlets", label: "Replace Outlets / Switches" },
      { value: "tv_mounting", label: "TV Mounting" },
      { value: "smart_switches", label: "Install Smart Switches / Thermostats" },
      { value: "mount_shelves", label: "Mount Shelves, Mirrors, or Artwork" },
      { value: "install_curtain_rods", label: "Install Curtain Rods or Shades" },
      { value: "power_washing", label: "Power Washing (driveways, patios)" },
      { value: "weatherizing", label: "Weatherizing (caulking, door sweeps)" },
      { value: "rental_turnover", label: "Rental Turnover Repairs" },
      { value: "install_dishwasher", label: "Install Dishwasher (replacement)" },
      { value: "install_microwave", label: "Install Over-the-Range Microwave" }
    ]
  },
  {
    id: "blinds_quantity",
    question: "How many blinds need to be hung?",
    type: "radio",
    showIf: { interior_repair_services: "hang_blinds" },
    options: [
      { value: "1", label: "1 blind" },
      { value: "2-3", label: "2–3 blinds" },
      { value: "4-6", label: "4–6 blinds" },
      { value: "7+", label: "7+ blinds" }
    ]
  },
  {
    id: "outlets_quantity",
    question: "How many outlets or switches?",
    type: "radio",
    showIf: { interior_repair_services: "replace_outlets" },
    options: [
      { value: "1", label: "1" },
      { value: "2-3", label: "2–3" },
      { value: "4-6", label: "4–6" },
      { value: "7+", label: "7+" }
    ]
  },
  {
    id: "rental_turnover_scope",
    question: "What's the extent of the turnover repairs?",
    type: "radio",
    showIf: { interior_repair_services: "rental_turnover" },
    options: [
      { value: "light", label: "Light (1-2 hours, minor touch-ups)" },
      { value: "moderate", label: "Moderate (half day, multiple tasks)" },
      { value: "extensive", label: "Extensive (full day+, comprehensive)" }
    ]
  },
  {
    id: "interior_repair_description",
    question: "Please describe the work needed (optional):",
    type: "text",
    placeholder: "Any additional details about the repairs..."
  }
];

// Hanging/Mounting Questions
export const MOUNTING_QUESTIONS = [
  {
    id: "mount_items",
    question: "What needs to be mounted or assembled? (Select all that apply)",
    type: "checkbox",
    options: [
      { value: "curtains_blinds", label: "Curtains / Blinds — $75–$200 per set" },
      { value: "furniture", label: "Furniture (bed, desk, dresser, etc.) — $75–$250 per item" },
      { value: "mirrors_artwork", label: "Mirrors / Artwork — $50–$125 per item" },
      { value: "shelves", label: "Shelves — $50–$150 per shelf" },
      { value: "tv_mount", label: "TV (wall mount) — $200–$300 per TV" },
      { value: "other_mounting", label: "Other — $75–$300 (description required)" }
    ]
  },
  {
    id: "mount_item_count",
    question: "How many items total?",
    type: "radio",
    options: [
      { value: "1", label: "1 item — $75–$200" },
      { value: "2-3", label: "2–3 items — $150–$400" },
      { value: "4-6", label: "4–6 items — $300–$750" },
      { value: "7+", label: "7+ items — $600–$1,200" }
    ]
  },
  {
    id: "wall_type",
    question: "What type of wall?",
    type: "radio",
    options: [
      { value: "drywall", label: "Drywall — standard pricing" },
      { value: "brick", label: "Brick — +$25–$75" },
      { value: "concrete", label: "Concrete — +$50–$125" },
      { value: "not_sure", label: "Not sure — assume drywall (contractor confirms on site)" }
    ]
  },
  {
    id: "tv_size",
    question: "What size TV?",
    type: "radio",
    showIf: { mount_items: "tv_mount" },
    options: [
      { value: "under_55", label: "Under 55\"" },
      { value: "55-64", label: "55\" – 64\"" },
      { value: "65_plus", label: "65\"+ (may increase estimate)" }
    ]
  },
  {
    id: "furniture_anchoring",
    question: "Does the furniture require wall anchoring?",
    type: "radio",
    showIf: { mount_items: "furniture" },
    options: [
      { value: "yes", label: "Yes — +$25–$75" },
      { value: "no", label: "No" }
    ]
  },
  {
    id: "other_mount_description",
    question: "Please describe what you need mounted or assembled:",
    type: "text",
    showIf: { mount_items: "other_mounting" },
    placeholder: "e.g., ceiling fan, basketball hoop, heavy mirror, etc."
  }
];

// Doors & Locks Questions
export const DOORS_LOCKS_QUESTIONS = [
  {
    id: "door_lock_work",
    question: "What door or lock work is needed? (Select all that apply)",
    type: "checkbox",
    options: [
      { value: "door_adjustment", label: "Door adjustment (sticking / alignment) — $75–$150" },
      { value: "door_hardware", label: "Door hardware replacement — $75–$200" },
      { value: "interior_door_install", label: "Interior door install — $150–$350" },
      { value: "lock_replacement", label: "Lock replacement / rekey — $75–$200" },
      { value: "smart_lock", label: "Smart lock installation — $150–$300" },
      { value: "exterior_door_install", label: "Exterior door install — $250–$600" },
      { value: "other_door", label: "Other — $100–$500 (description required)" }
    ]
  },
  {
    id: "door_lock_count",
    question: "How many doors or locks total?",
    type: "radio",
    options: [
      { value: "1", label: "1 — $75–$200" },
      { value: "2-3", label: "2–3 — $200–$450" },
      { value: "4-6", label: "4–6 — $400–$900" },
      { value: "7+", label: "7+ — $800–$1,500" }
    ]
  },
  {
    id: "is_exterior_door",
    question: "Is this an exterior door?",
    type: "radio",
    showIf: { door_lock_work: "interior_door_install" },
    options: [
      { value: "yes", label: "Yes (may increase estimate)" },
      { value: "no", label: "No, interior only" }
    ]
  },
  {
    id: "framing_modification",
    question: "Is framing modification required?",
    type: "radio",
    showIf: { door_lock_work: "exterior_door_install" },
    options: [
      { value: "yes", label: "Yes — +$100–$300" },
      { value: "no", label: "No" },
      { value: "not_sure", label: "Not sure (contractor will assess)" }
    ]
  },
  {
    id: "other_door_description",
    question: "Please describe the door or lock work needed:",
    type: "text",
    showIf: { door_lock_work: "other_door" },
    placeholder: "e.g., pet door install, garage door adjustment, etc."
  }
];

// Carpentry Questions
export const CARPENTRY_QUESTIONS = [
  {
    id: "carpentry_work",
    question: "What carpentry work is needed? (Select all that apply)",
    type: "checkbox",
    options: [
      { value: "baseboards_trim", label: "Baseboards / trim repair — $100–$300" },
      { value: "cabinet_repair", label: "Cabinet repair / adjustment — $125–$400" },
      { value: "closet_shelving", label: "Closet shelving install — $150–$500" },
      { value: "deck_repair", label: "Deck repair (non-structural) — $200–$600" },
      { value: "drywall_repair", label: "Drywall repair (small sections) — $100–$350" },
      { value: "framing_repair", label: "Framing repair (minor) — $250–$800" },
      { value: "other_carpentry", label: "Other — $150–$1,000 (description required)" }
    ]
  },
  {
    id: "carpentry_scope",
    question: "Scope of work?",
    type: "radio",
    options: [
      { value: "small", label: "Small (1–2 hours) — $100–$300" },
      { value: "medium", label: "Medium (half day) — $300–$700" },
      { value: "large", label: "Large (full day) — $700–$1,200" }
    ]
  },
  {
    id: "drywall_finishing",
    question: "Is drywall finishing required?",
    type: "radio",
    showIf: { carpentry_work: "drywall_repair" },
    options: [
      { value: "yes", label: "Yes — +$50–$150" },
      { value: "no", label: "No" }
    ]
  },
  {
    id: "painting_included",
    question: "Is painting included?",
    type: "radio",
    options: [
      { value: "yes", label: "Yes — +$100–$300" },
      { value: "no", label: "No (paint separately or leave as-is)" }
    ]
  },
  {
    id: "other_carpentry_description",
    question: "Please describe the carpentry work needed:",
    type: "text",
    showIf: { carpentry_work: "other_carpentry" },
    placeholder: "e.g., custom shelf, fence repair, pergola touch-up, etc."
  }
];

// Small Electrical Questions
export const SMALL_ELECTRICAL_QUESTIONS = [
  {
    id: "electrical_work",
    question: "What electrical work is needed? (Select all that apply)",
    type: "checkbox",
    options: [
      { value: "ceiling_fan", label: "Ceiling fan install / replacement — $150–$350" },
      { value: "light_fixture", label: "Light fixture install — $100–$250" },
      { value: "outlet_replacement", label: "Outlet replacement — $80–$150" },
      { value: "smart_device", label: "Smart device install (switch, thermostat) — $125–$300" },
      { value: "switch_replacement", label: "Switch replacement — $80–$150" },
      { value: "troubleshooting", label: "Troubleshooting (minor) — $100–$250" },
      { value: "other_electrical", label: "Other — $100–$400 (description required)" }
    ]
  },
  {
    id: "electrical_fixture_count",
    question: "How many fixtures or devices?",
    type: "radio",
    options: [
      { value: "1", label: "1 — $80–$150" },
      { value: "2-3", label: "2–3 — $200–$450" },
      { value: "4-6", label: "4–6 — $400–$900" },
      { value: "7+", label: "7+ — $800–$1,400" }
    ]
  },
  {
    id: "is_ceiling_fixture",
    question: "Is this a ceiling fixture?",
    type: "radio",
    showIf: { electrical_work: "light_fixture" },
    options: [
      { value: "yes", label: "Yes (ladder required) — +$25–$75" },
      { value: "no", label: "No" }
    ]
  },
  {
    id: "exposed_wiring",
    question: "Is there any exposed wiring?",
    type: "radio",
    options: [
      { value: "yes", label: "Yes — +$50–$100" },
      { value: "no", label: "No" },
      { value: "not_sure", label: "Not sure (contractor will assess)" }
    ]
  },
  {
    id: "other_electrical_description",
    question: "Please describe the electrical work needed:",
    type: "text",
    showIf: { electrical_work: "other_electrical" },
    placeholder: "e.g., doorbell install, under-cabinet lights, etc."
  }
];

// Small Plumbing Questions
export const SMALL_PLUMBING_QUESTIONS = [
  {
    id: "plumbing_work",
    question: "What plumbing work is needed? (Select all that apply)",
    type: "checkbox",
    options: [
      { value: "faucet_replacement", label: "Faucet replacement — $100–$250" },
      { value: "garbage_disposal", label: "Garbage disposal install — $150–$350" },
      { value: "leak_repair", label: "Leak repair (visible / minor) — $100–$300" },
      { value: "showerhead_replacement", label: "Showerhead replacement — $75–$150" },
      { value: "toilet_repair", label: "Toilet repair — $100–$300" },
      { value: "toilet_replacement", label: "Toilet replacement — $200–$500" },
      { value: "other_plumbing", label: "Other — $125–$500 (description required)" }
    ]
  },
  {
    id: "plumbing_fixture_count",
    question: "How many fixtures total?",
    type: "radio",
    options: [
      { value: "1", label: "1 — $75–$150" },
      { value: "2-3", label: "2–3 — $200–$450" },
      { value: "4-6", label: "4–6 — $400–$900" },
      { value: "7+", label: "7+ — $800–$1,500" }
    ]
  },
  {
    id: "active_leak",
    question: "Is there active leaking?",
    type: "radio",
    showIf: { plumbing_work: "leak_repair" },
    options: [
      { value: "yes", label: "Yes (may escalate urgency) — +$50–$150" },
      { value: "no", label: "No, it's intermittent or contained" }
    ]
  },
  {
    id: "water_shutoff",
    question: "Is the water shutoff accessible?",
    type: "radio",
    options: [
      { value: "yes", label: "Yes" },
      { value: "no", label: "No / Not sure" }
    ]
  },
  {
    id: "other_plumbing_description",
    question: "Please describe the plumbing work needed:",
    type: "text",
    showIf: { plumbing_work: "other_plumbing" },
    placeholder: "e.g., water heater flush, outdoor spigot repair, etc."
  }
];

// Generic Project Size Question (for services without detailed flows)
export const PROJECT_SIZE_QUESTION = {
  id: "project_size",
  question: "How would you describe the size of your project?",
  type: "radio",
  options: [
    { value: "small", label: "Small - Minor repair or single item (1-2 hours)" },
    { value: "medium", label: "Medium - Multiple items or tasks (3-5 hours)" },
    { value: "large", label: "Large - Full day or multi-day project" }
  ]
};

// Timeline Question
export const TIMELINE_QUESTION = {
  id: "timeline",
  question: "When do you need this done?",
  type: "radio",
  options: HANDYMAN_TIMELINE_OPTIONS
};
